import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AddCartItemRequest, BillingCycle, CartDto } from "@/types/api";

const CART_KEY = ["cart"] as const;

/** The current user's active cart. Authenticated-only. */
export function useCart() {
  return useQuery({
    queryKey: CART_KEY,
    queryFn: () => api.get<CartDto>("/api/cart"),
    staleTime: 10_000
  });
}

export function useAddToCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddCartItemRequest) =>
      api.post<CartDto>("/api/cart/items", body),
    onSuccess: (cart) => qc.setQueryData(CART_KEY, cart)
  });
}

export function useUpdateCartItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      quantity?: number;
      billing_cycle?: BillingCycle;
      domain?: string;
    }) =>
      api.patch<CartDto>(`/api/cart/items/${vars.id}`, {
        quantity: vars.quantity,
        billing_cycle: vars.billing_cycle,
        domain: vars.domain
      }),
    onSuccess: (cart) => qc.setQueryData(CART_KEY, cart)
  });
}

export function useRemoveCartItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<CartDto>(`/api/cart/items/${id}`),
    onSuccess: (cart) => qc.setQueryData(CART_KEY, cart)
  });
}

export function useClearCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<CartDto>("/api/cart"),
    onSuccess: (cart) => qc.setQueryData(CART_KEY, cart)
  });
}
